import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarClock,
  CalendarDays,
  Gavel,
  Inbox,
  LayoutDashboard,
  ListTodo,
  Radar,
  RefreshCw,
  Users,
  type LucideIcon,
} from 'lucide-react';

import lexiUrl from '@/assets/landing/lexi.webp';

import {
  AppFrame,
  FrameBadge,
  FrameButton,
  FramePageHeader,
  type BadgeTone,
  type PanelItem,
} from './app-frame';
import { ScaledCanvas } from './scaled-canvas';

/*
 * O "Painel de hoje" do CRM (features/dashboard), com os mesmos blocos e
 * textos: atalhos rápidos, resumo da operação e os cartões de prazos,
 * tarefas e intimações. Dados fictícios.
 */

const panelItems: PanelItem[] = [
  { label: 'Início', icon: LayoutDashboard, active: true },
  { label: 'Clientes', icon: Users },
  { label: 'Processos', icon: Gavel, nested: true },
  { label: 'Prazos', icon: CalendarClock, nested: true },
  { label: 'Agenda', icon: CalendarDays, nested: true },
  { label: 'Tarefas', icon: ListTodo, nested: true },
  { label: 'Publicações', icon: Radar, badge: 5 },
];

const quickActions: Array<[string, string, LucideIcon]> = [
  ['Prazos', 'Ver datas fatais', CalendarClock],
  ['Tarefas', 'Organizar trabalho', ListTodo],
  ['Intimações', 'Fazer a triagem', Inbox],
  ['Novo processo', 'Iniciar cadastro', BriefcaseBusiness],
];

const summary: Array<[string, string, string, LucideIcon, string]> = [
  [
    '3',
    'Prazos de hoje',
    'vencimentos na data fatal',
    CalendarClock,
    'bg-error-container text-error',
  ],
  [
    '12',
    'Tarefas pendentes',
    'itens ainda em aberto',
    ListTodo,
    'bg-tertiary-fixed text-on-tertiary-fixed-variant',
  ],
  [
    '5',
    'Intimações novas',
    'aguardando triagem',
    Inbox,
    'bg-secondary-fixed text-on-secondary-fixed',
  ],
];

interface Row {
  title: string;
  /** Selo antes do título (tribunal) ou depois dele (prioridade). */
  lead?: [BadgeTone, string];
  trail?: [BadgeTone, string];
  meta: string;
  detail: string;
}

interface ColumnCard {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconClass: string;
  count: [BadgeTone, number];
  rows: Row[];
}

const columns: ColumnCard[] = [
  {
    title: 'Prazos de hoje',
    subtitle: 'Data fatal em 02/10/2026',
    icon: CalendarClock,
    iconClass: 'text-error',
    count: ['error', 3],
    rows: [
      {
        title: 'Contestação',
        meta: 'Costa × Construtora Alfa · Marina Costa',
        detail: 'Responsável: Ana Costa',
      },
      {
        title: 'Réplica',
        meta: 'Almeida × Banco Meridiano · João Almeida',
        detail: 'Responsável: Pedro Henrique',
      },
      {
        title: 'Manifestação sobre o laudo',
        meta: 'Lima × Transportes Rota · Fernanda Lima',
        detail: 'Responsável: Ana Costa',
      },
    ],
  },
  {
    title: 'Tarefas pendentes',
    subtitle: 'Ordenadas por prioridade',
    icon: ListTodo,
    iconClass: 'text-tertiary',
    count: ['warning', 12],
    rows: [
      {
        title: 'Protocolar recurso',
        trail: ['error', 'Alta'],
        meta: 'Nunes × Metalúrgica Sul · Luana Melo',
        detail: 'Vence hoje',
      },
      {
        title: 'Revisar procuração',
        trail: ['warning', 'Média'],
        meta: 'Rotina do escritório · Pedro Henrique',
        detail: 'Vence em 05/10/2026',
      },
      {
        title: 'Ligar para cliente',
        trail: ['neutral', 'Baixa'],
        meta: 'Grupo Vértice · Ana Costa',
        detail: 'Vence em 07/10/2026',
      },
    ],
  },
  {
    title: 'Intimações novas',
    subtitle: 'Aguardando triagem na inbox',
    icon: Inbox,
    iconClass: 'text-secondary',
    count: ['info', 5],
    rows: [
      {
        title: 'Intimação',
        lead: ['info', 'TJSP'],
        meta: 'Costa × Construtora Alfa',
        detail: 'Recebida em 02/10/2026 07:58',
      },
      {
        title: 'Designação de audiência',
        lead: ['info', 'TRT-2'],
        meta: 'Lima × Transportes Rota',
        detail: 'Recebida em 02/10/2026 07:58',
      },
      {
        title: 'Despacho',
        lead: ['info', 'TJMG'],
        meta: 'Sem processo vinculado',
        detail: 'Recebida em 01/10/2026 18:12',
      },
    ],
  },
];

function PainelDeHoje() {
  return (
    <div className="space-y-6">
      <FramePageHeader
        eyebrow="Operação diária"
        title="Painel de hoje"
        description="Prazos, tarefas e intimações que precisam da atenção do escritório."
        actions={
          <FrameButton variant="outline">
            <RefreshCw aria-hidden="true" /> Atualizar
          </FrameButton>
        }
      />

      <div>
        <p className="mb-2 text-sm font-semibold">Atalhos rápidos</p>
        <div className="grid grid-cols-4 gap-2">
          {quickActions.map(([label, description, Icon]) => (
            <div
              key={label}
              className="border-outline-variant bg-surface-container-lowest flex items-center gap-3 rounded-xl border px-3 py-3"
            >
              <span className="bg-primary-container text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                <Icon className="size-4.5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{label}</span>
                <span className="text-on-surface-variant block truncate text-xs">
                  {description}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {summary.map(([value, label, detail, Icon, iconClass]) => (
            <div
              key={label}
              className="border-outline-variant bg-surface-container-lowest flex items-center gap-4 rounded-xl border p-4"
            >
              <span
                className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${iconClass}`}
              >
                <Icon className="size-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-2xl font-semibold tabular-nums">{value}</span>
                <span className="block text-sm font-medium">{label}</span>
                <span className="text-on-surface-variant block truncate text-xs">{detail}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-4">
          {columns.map(({ title, subtitle, icon: Icon, iconClass, count, rows }) => (
            <div
              key={title}
              className="border-outline-variant bg-surface-container-lowest overflow-hidden rounded-xl border"
            >
              <div className="border-outline-variant flex items-center justify-between gap-3 border-b px-5 py-4">
                <div>
                  <p className="text-body-md flex items-center gap-2 font-semibold">
                    <Icon className={`size-4.5 ${iconClass}`} aria-hidden="true" />
                    {title}
                  </p>
                  <p className="text-on-surface-variant mt-0.5 text-xs">{subtitle}</p>
                </div>
                <FrameBadge tone={count[0]}>{count[1]}</FrameBadge>
              </div>
              <ul className="divide-outline-variant divide-y">
                {rows.map(({ title: rowTitle, lead, trail, meta, detail }) => (
                  <li key={rowTitle} className="flex items-center gap-3 px-5 py-3.5">
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        {lead && <FrameBadge tone={lead[0]}>{lead[1]}</FrameBadge>}
                        <span className="truncate text-sm font-medium">{rowTitle}</span>
                        {trail && <FrameBadge tone={trail[0]}>{trail[1]}</FrameBadge>}
                      </span>
                      <span className="text-on-surface-variant mt-0.5 block truncate text-xs">
                        {meta}
                      </span>
                      <span className="text-on-surface-variant mt-1 block text-xs">{detail}</span>
                    </span>
                    <ArrowRight className="text-outline size-4 shrink-0" aria-hidden="true" />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/*
 * A Lexi apoiada na borda de cima do painel, da cintura para cima: o corte cai
 * na linha do card e ela fica toda acima dele. Medidas em pixels da tela de
 * desenho, multiplicadas por `--canvas-scale` para acompanhar o painel.
 */
const LEXI_WIDTH = 196;
/** Altura que aparece: da cabeça até a cintura (74% da imagem). */
const LEXI_VISIBLE = 166;

function LexiPeek() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-0 z-10 -translate-x-1/2 overflow-hidden"
      style={{
        width: `calc(var(--canvas-scale) * ${LEXI_WIDTH}px)`,
        height: `calc(var(--canvas-scale) * ${LEXI_VISIBLE}px)`,
      }}
    >
      <img src={lexiUrl} alt="" width="414" height="475" className="block h-auto w-full" />
    </div>
  );
}

/** A tela de entrada do CRM, desenhada para o hero. */
export function HeroDashboard() {
  return (
    <ScaledCanvas
      width={1280}
      height={780}
      minScale={0.5}
      label="Painel de hoje do Sancus, com atalhos rápidos, resumo da operação e os cartões de prazos de hoje, tarefas pendentes e intimações novas. Dados fictícios."
      className="border-outline-variant rounded-[20px] border bg-white shadow-[0_40px_100px_-40px_rgb(12_80_227/0.35)]"
      overlay={<LexiPeek />}
      overlaySpace={LEXI_VISIBLE}
    >
      <AppFrame area="Trabalho" items={panelItems} section="Início">
        <PainelDeHoje />
      </AppFrame>
    </ScaledCanvas>
  );
}
